/**
 * GMA-1617 (CompassCare Web 2/14 — Overview) — one KPI tile.
 *
 * The Overview's KPI strip is 7 of these. Each tile is ALSO a filter: clicking
 * it scopes the client grid, so a tile is a real <button> with a pressed state
 * (aria-pressed reflects `active`). The numeric value is formatted via the
 * shared webFormat (IBM Plex Mono numerics); an optional `severity` tints the
 * value from the single design-token set (e.g. the Active Alerts tile in red).
 *
 * a11y: accessible name = "<label>: <value>[ <unit>]"; 44px min hit target;
 * when no onSelect is provided the tile is a non-interactive, disabled button
 * (no focusable-but-inert control). Fail-safe: a missing/NaN value renders "—".
 */
import React from "react";
import PropTypes from "prop-types";
import { theme, severityToken } from "../../styles/designTokens";
import { formatNumber, PLACEHOLDER } from "../../utils/webFormat";

export default function KpiTile({
  label,
  value,
  unit,
  severity,
  active = false,
  onSelect,
}) {
  // Numbers → grouped via webFormat (fail-safe); strings pass through; missing
  // → placeholder. Never renders "NaN"/"undefined".
  const displayValue =
    value === null || value === undefined
      ? PLACEHOLDER
      : typeof value === "number"
        ? formatNumber(value)
        : String(value);

  // Accent from the token set; severityToken is fail-safe (unknown → normal).
  const accent = severity ? severityToken(severity).color : theme.textPrimary;

  const selectable = typeof onSelect === "function";
  const accessibleName = unit
    ? `${label}: ${displayValue} ${unit}`
    : `${label}: ${displayValue}`;

  return (
    <button
      type="button"
      data-testid="kpi-tile"
      data-kpi-active={active ? "true" : "false"}
      aria-pressed={active}
      aria-label={accessibleName}
      disabled={!selectable}
      onClick={selectable ? () => onSelect() : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
        minWidth: 120,
        minHeight: 64,
        padding: "12px 16px",
        textAlign: "left",
        cursor: selectable ? "pointer" : "default",
        background: active ? theme.surface : theme.bg,
        border: `1px solid ${active ? accent : theme.border}`,
        borderRadius: 8,
        color: theme.textMuted,
        fontFamily: theme.fontUI,
      }}
    >
      <span style={{ fontSize: 12, color: theme.textMuted }}>{label}</span>
      <span
        aria-hidden="true"
        style={{
          fontFamily: theme.fontMono,
          fontSize: 22,
          lineHeight: 1,
          color: accent,
        }}
      >
        {displayValue}
        {unit ? (
          <span style={{ fontSize: 12, color: theme.textMuted }}> {unit}</span>
        ) : null}
      </span>
    </button>
  );
}

KpiTile.propTypes = {
  /** Tile caption, e.g. "Active Alerts". */
  label: PropTypes.string.isRequired,
  /** Number (grouped via webFormat) or pre-formatted string; null/undefined → "—". */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Optional unit shown after the value (e.g. "min", "online"). */
  unit: PropTypes.string,
  /** Optional severity level tinting the value from the token set. */
  severity: PropTypes.string,
  /** Whether this tile's filter is currently applied (aria-pressed). */
  active: PropTypes.bool,
  /** Called when the tile is clicked to apply its filter; absent → disabled. */
  onSelect: PropTypes.func,
};
